import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';
const BASE = `${API_URL}/api/AdministrarEmpresas`;

export interface EmpresaUsuario {
    usuarioId: number;
    nome: string;
    email: string;
    tipoUsuarioId: number;
    ativo: boolean;
    /** Convite por e-mail (05/10/2026). */
    convitePendente?: boolean;
    conviteExpirado?: boolean;
    /** Pedidos no cadastro (16/10/2026). Só dígitos, com DDI; nulo em quem entrou antes. */
    telefone?: string | null;
    aceitouMarketingEm?: string | null;
    /** Boas-vindas (28/10/2026): "concluido" ou "pulado-N", só em quem criou a conta; nulo nos demais. */
    boasVindas?: string | null;
    boasVindasEm?: string | null;
}

/** A API devolve `usuarios[]` no formato do UsuarioResponseDTO (userId, userName…);
 *  esta função traduz para o que a tela usa. Aceita também o formato já traduzido. */
export const paraEmpresaUsuario = (u: any): EmpresaUsuario => ({
    usuarioId: u.usuarioId ?? u.userId,
    nome: u.nome ?? u.userName ?? '',
    email: u.email ?? u.userEmail ?? '',
    tipoUsuarioId: u.tipoUsuarioId ?? u.userTypeuserid ?? 0,
    ativo: u.ativo ?? !u.userDeleted,
    convitePendente: !!u.convitePendente,
    conviteExpirado: !!u.conviteExpirado,
    telefone: u.telefone ?? u.userPhone ?? null,
    aceitouMarketingEm: u.aceitouMarketingEm ?? u.userMarketingConsentAt ?? null,
    boasVindas: u.boasVindas ?? null,
    boasVindasEm: u.boasVindasEm ?? null,
});

export interface Empresa {
    id: number;
    nome: string;
    cnpj: string;
    dataAssinaturaExpira: string;
    ativo?: boolean;
    /** Quando a empresa foi criada na plataforma. */
    dataCadastro?: string | null;
    /** Primeiro administrador ativo cadastrado; nulo se a empresa não tem nenhum. */
    administrador?: string | null;
    /** Celular do administrador acima, pedido no cadastro: só dígitos, com DDI. Nulo em conta antiga. */
    administradorTelefone?: string | null;
    usuarios?: EmpresaUsuario[];
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export interface CriarEmpresaDTO {
    nome: string;
    cnpj: string;
    dataAssinaturaExpira: string | null;
}

export interface AtualizarEmpresaDTO {
    id: number;
    nome: string;
    cnpj: string;
    dataAssinaturaExpira: string | null;
}

/** Convite por e-mail (05/10/2026): a senha saiu, a pessoa cria a própria pelo link. */
export interface CriarUsuarioEmpresaDTO {
    empresaId: number;
    nome: string;
    email: string;
    tipoUsuarioId: number;
}

export interface AtualizarUsuarioEmpresaDTO {
    usuarioId: number;
    empresaId: number;
    nome: string;
    email: string;
    tipoUsuarioId: number;
    ativo: boolean;
}

export interface AdministradorAtivo {
    userId: number;
    userName: string;
    userEmail: string;
    userBranchid: number;
    branchName: string;
    userTypeuserid: number;
    userDeleted: boolean;
    photoUrl: string | null;
}

export interface AssumirControleDTO {
    empresaId: number;
    usuarioId: number;
}

export interface AssumirControleResult {
    empresaId: number;
    usuarioId: number;
    nomeUsuario: string;
}

export const EmpresaService = {
    buscarTodas: async (): Promise<ApiResponse<Empresa[]>> => {
        const response = await apiFetch(`${BASE}/BuscarTodasEmpresas`);
        if (response.status === 204) return { sucesso: true, mensagem: '', dados: [] };
        if (!response.ok) throw new Error('Erro ao buscar empresas');
        return response.json();
    },

    buscarPorId: async (empresaId: number): Promise<ApiResponse<Empresa>> => {
        const response = await apiFetch(`${BASE}/BuscarEmpresaPorId/${empresaId}`);
        if (!response.ok) throw new Error('Erro ao buscar empresa');
        return response.json();
    },

    criar: async (data: CriarEmpresaDTO): Promise<ApiResponse<Empresa>> => {
        const response = await apiFetch(`${BASE}/CriarEmpresa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Erro ao criar empresa');
        return response.json();
    },

    atualizar: async (data: AtualizarEmpresaDTO): Promise<ApiResponse<Empresa>> => {
        const response = await apiFetch(`${BASE}/AtualizarEmpresa`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Erro ao atualizar empresa');
        return response.json();
    },

    desativar: async (empresaId: number): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${BASE}/DesativarEmpresa/${empresaId}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Erro ao desativar empresa');
        return response.json();
    },

    criarUsuario: async (data: CriarUsuarioEmpresaDTO): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${BASE}/CriarUsuarioEmpresa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Erro ao criar usuário da empresa');
        return response.json();
    },

    atualizarUsuario: async (data: AtualizarUsuarioEmpresaDTO): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${BASE}/AtualizarUsuarioEmpresa`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Erro ao atualizar usuário da empresa');
        return response.json();
    },

    buscarAdministradoresAtivos: async (empresaId: number): Promise<ApiResponse<AdministradorAtivo[]>> => {
        const response = await apiFetch(`${BASE}/BuscarAdministradoresAtivos/${empresaId}`);
        const result = await response.json();
        if (!response.ok && response.status !== 400) throw new Error(result?.mensagem || 'Erro ao buscar administradores da empresa');
        return result;
    },

    assumirControle: async (data: AssumirControleDTO): Promise<ApiResponse<AssumirControleResult>> => {
        const response = await apiFetch(`${BASE}/AssumirControleEmpresa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result?.mensagem || 'Erro ao assumir controle da empresa');
        return result;
    },
};
